/**
 * Personnel Accounting Report Panel — Muhasebe Bildirimi UI.
 *
 * Uses ONLY existing backend endpoints:
 *   GET  /api/personnel/accounting-report?year&month
 *   GET  /api/personnel/accounting-report/pdf?year&month
 *   POST /api/personnel/accounting-report/send-email
 *   POST /api/personnel/accounting-report/send-telegram
 *   GET  /api/personnel/accounting-report/history?year&month
 *
 * NO frontend aggregation. Backend response is source-of-truth.
 * NO frontend PDF/email/Telegram logic.
 */
import React, { useEffect, useMemo, useState } from "react";
import { api } from "@/lib/api";
import { Card, Btn, Modal, Input, Textarea } from "@/components/UI";
import { FileText, Mail, Send, RefreshCw, Download, History } from "lucide-react";

const TR_MONTHS = [
  "Ocak", "Şubat", "Mart", "Nisan", "Mayıs", "Haziran",
  "Temmuz", "Ağustos", "Eylül", "Ekim", "Kasım", "Aralık",
];

const fmtMoney = (v) => {
  if (!v) return "—";
  return `${Number(v).toLocaleString("tr-TR", { minimumFractionDigits: 2 })} TL`;
};

const fmtDateTime = (iso) => {
  if (!iso) return "-";
  try {
    const d = new Date(iso);
    return `${String(d.getDate()).padStart(2, "0")}.${String(d.getMonth() + 1).padStart(2, "0")}.${d.getFullYear()} ${String(d.getHours()).padStart(2, "0")}:${String(d.getMinutes()).padStart(2, "0")}`;
  } catch { return iso; }
};

export default function AccountingReportPanel() {
  const now = new Date();
  const [year, setYear] = useState(now.getFullYear());
  const [month, setMonth] = useState(now.getMonth() + 1);
  const [report, setReport] = useState(null);
  const [loading, setLoading] = useState(false);
  const [err, setErr] = useState(null);
  const [emailOpen, setEmailOpen] = useState(false);
  const [tgOpen, setTgOpen] = useState(false);
  const [history, setHistory] = useState([]);

  const loadReport = async () => {
    setLoading(true); setErr(null); setReport(null);
    try {
      const r = await api.get(`/personnel/accounting-report?year=${year}&month=${month}`);
      setReport(r);
    } catch (e) {
      setErr(e?.response?.data?.detail || "Rapor yüklenemedi");
    } finally { setLoading(false); }
  };

  const loadHistory = async () => {
    try {
      const r = await api.get(`/personnel/accounting-report/history?year=${year}&month=${month}`);
      setHistory(r.rows || []);
    } catch { setHistory([]); }
  };

  useEffect(() => { loadHistory(); /* eslint-disable-next-line */ }, [year, month]);

  const downloadPdf = async () => {
    try {
      const backend = process.env.REACT_APP_BACKEND_URL;
      const token = localStorage.getItem("token");
      const url = `${backend}/api/personnel/accounting-report/pdf?year=${year}&month=${month}`;
      const res = await fetch(url, { headers: { Authorization: `Bearer ${token}` } });
      if (!res.ok) throw new Error("PDF indirilemedi");
      const blob = await res.blob();
      const u = URL.createObjectURL(blob);
      const a = document.createElement("a");
      a.href = u;
      a.download = `MuhasebeBildirimi_${year}_${String(month).padStart(2, "0")}.pdf`;
      document.body.appendChild(a); a.click(); a.remove();
      URL.revokeObjectURL(u);
    } catch (e) {
      alert("PDF indirilemedi: " + e.message);
    }
  };

  const rows = report?.rows || [];
  const totalRows = rows.length;

  return (
    <Card className="p-4 mt-4" data-testid="acc-report-panel">
      <div className="flex items-center gap-2 mb-3">
        <FileText size={18} className="text-emerald-600" />
        <h3 className="text-base font-semibold text-slate-800">Muhasebe Bildirimi</h3>
        <span className="ml-auto text-xs text-slate-500">
          Aylık personel muhasebe raporu — mevcut backend endpointleri kullanılır.
        </span>
      </div>

      <div className="flex flex-wrap items-end gap-3 mb-3">
        <label className="text-xs">
          <div className="text-slate-500 uppercase tracking-wider mb-1">Ay</div>
          <select value={month} onChange={e => setMonth(Number(e.target.value))}
                  data-testid="acc-month-select"
                  className="border border-slate-200 rounded px-2 py-1.5 text-sm min-w-[120px]">
            {TR_MONTHS.map((m, i) => (
              <option key={i + 1} value={i + 1}>{m}</option>
            ))}
          </select>
        </label>
        <label className="text-xs">
          <div className="text-slate-500 uppercase tracking-wider mb-1">Yıl</div>
          <input type="number" value={year} min="2020" max="2100"
                 onChange={e => setYear(Number(e.target.value))}
                 data-testid="acc-year-input"
                 className="border border-slate-200 rounded px-2 py-1.5 text-sm w-24" />
        </label>
        <Btn onClick={loadReport} disabled={loading}
             data-testid="acc-generate-btn"
             className="bg-emerald-600 hover:bg-emerald-700 text-white">
          {loading ? <RefreshCw size={14} className="animate-spin" /> : <FileText size={14} />}
          Rapor Oluştur
        </Btn>
        {report && (
          <>
            <Btn variant="secondary" onClick={downloadPdf}
                 data-testid="acc-pdf-btn">
              <Download size={14} /> PDF İndir
            </Btn>
            <Btn variant="secondary" onClick={() => setEmailOpen(true)}
                 data-testid="acc-email-btn">
              <Mail size={14} /> E-posta Gönder
            </Btn>
            <Btn variant="secondary" onClick={() => setTgOpen(true)}
                 data-testid="acc-telegram-btn">
              <Send size={14} /> Telegram Gönder
            </Btn>
          </>
        )}
      </div>

      {err && <div className="text-sm text-rose-700 mb-2">{err}</div>}

      {report && (
        <div data-testid="acc-report-summary">
          <div className="text-xs text-slate-500 mb-2">
            Dönem: <b>{TR_MONTHS[month - 1]} {year}</b> · Hareketi olan personel: <b>{totalRows}</b>
          </div>
          {totalRows === 0 && (
            <div className="text-sm text-slate-500 italic py-3">
              Bu ay için raporlanacak personel hareketi bulunmamaktadır.
            </div>
          )}
          {totalRows > 0 && (
            <div className="border rounded overflow-x-auto">
              <table className="w-full text-sm">
                <thead className="bg-slate-50 text-xs uppercase tracking-wider text-slate-600">
                  <tr>
                    <th className="p-2 text-left">Personel</th>
                    <th className="p-2 text-right">Avans</th>
                    <th className="p-2 text-right">Mesai (saat)</th>
                    <th className="p-2 text-right">İzin (gün)</th>
                    <th className="p-2 text-right">Rapor (gün)</th>
                    <th className="p-2 text-right">Prim / Ek Ödeme</th>
                  </tr>
                </thead>
                <tbody>
                  {rows.map(r => (
                    <tr key={r.employee_id} className="border-t"
                        data-testid={`acc-row-${r.employee_id}`}>
                      <td className="p-2 font-medium">{r.employee_name}</td>
                      <td className="p-2 text-right font-mono">{fmtMoney(r.advance_try)}</td>
                      <td className="p-2 text-right font-mono">{r.overtime_hours ? r.overtime_hours.toFixed(1) : "—"}</td>
                      <td className="p-2 text-right font-mono">{r.leave_days ? r.leave_days.toFixed(0) : "—"}</td>
                      <td className="p-2 text-right font-mono">{r.report_days ? r.report_days.toFixed(0) : "—"}</td>
                      <td className="p-2 text-right font-mono">{fmtMoney(r.bonus_try)}</td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          )}

          {(report.new_hires?.length > 0 || report.exits?.length > 0) && (
            <div className="grid grid-cols-1 md:grid-cols-2 gap-3 mt-3">
              {report.new_hires?.length > 0 && (
                <div className="p-3 border border-emerald-200 bg-emerald-50/50 rounded">
                  <div className="text-xs font-semibold text-emerald-800 mb-1">İşe Girenler ({report.new_hires.length})</div>
                  {report.new_hires.map((h, i) => (
                    <div key={i} className="text-xs text-slate-700 py-0.5">
                      {h.name} — <span className="font-mono">{h.start_date}</span>
                    </div>
                  ))}
                </div>
              )}
              {report.exits?.length > 0 && (
                <div className="p-3 border border-rose-200 bg-rose-50/50 rounded">
                  <div className="text-xs font-semibold text-rose-800 mb-1">İşten Çıkanlar ({report.exits.length})</div>
                  {report.exits.map((x, i) => (
                    <div key={i} className="text-xs text-slate-700 py-0.5">
                      {x.name} — <span className="font-mono">{x.left_date}</span>
                      {x.reason ? ` — ${x.reason}` : ""}
                    </div>
                  ))}
                </div>
              )}
            </div>
          )}
        </div>
      )}

      <div className="mt-4 pt-3 border-t border-slate-200">
        <div className="flex items-center gap-2 mb-2">
          <History size={14} className="text-slate-500" />
          <span className="text-xs font-semibold uppercase tracking-wider text-slate-600">
            Gönderim Geçmişi ({history.length})
          </span>
          <button onClick={loadHistory}
                  className="ml-auto text-xs text-emerald-700 hover:underline"
                  data-testid="acc-history-refresh">
            Yenile
          </button>
        </div>
        {history.length === 0 && (
          <div className="text-xs text-slate-400 italic">Bu dönem için henüz gönderim kaydı yok.</div>
        )}
        {history.map((h, i) => (
          <div key={i} data-testid={`acc-hist-${i}`}
               className="text-xs py-1 border-b border-slate-100 flex flex-wrap gap-3">
            <span className="font-mono">{TR_MONTHS[h.month - 1]} {h.year}</span>
            <span className="text-slate-500">{fmtDateTime(h.at)}</span>
            <span className={`px-2 py-0.5 rounded ${h.channel === "EMAIL" ? "bg-blue-50 text-blue-700" : "bg-sky-50 text-sky-700"}`}>
              {h.channel === "EMAIL" ? "E-posta" : "Telegram"}
            </span>
            <span className="text-slate-700">{h.by_name || "-"}</span>
            {h.to && <span className="text-slate-500 font-mono">{h.to}</span>}
          </div>
        ))}
      </div>

      {emailOpen && (
        <EmailModal
          year={year} month={month}
          onClose={() => setEmailOpen(false)}
          onSent={() => { setEmailOpen(false); loadHistory(); }}
        />
      )}
      {tgOpen && (
        <TelegramModal
          year={year} month={month}
          onClose={() => setTgOpen(false)}
          onSent={() => { setTgOpen(false); loadHistory(); }}
        />
      )}
    </Card>
  );
}

function EmailModal({ year, month, onClose, onSent }) {
  const [toAddr, setToAddr] = useState("");
  const [subject, setSubject] = useState(`CustomerERP - Aylık Personel Muhasebe Bildirimi ${year}-${String(month).padStart(2, "0")}`);
  const [body, setBody] = useState("Ekte ilgili döneme ait personel muhasebe bildirimi bulunmaktadır.");
  const [sending, setSending] = useState(false);
  const [result, setResult] = useState(null);

  useEffect(() => {
    // Try to prefill accountant email from settings (from Muhasebeci Ayarları)
    api.get("/settings").then(cs => {
      const e = cs?.accountant_email;
      if (e) setToAddr(e);
    }).catch(() => {});
  }, []);

  const send = async () => {
    if (!toAddr.includes("@")) return alert("Geçerli bir e-posta adresi girin");
    setSending(true); setResult(null);
    try {
      const r = await api.post("/personnel/accounting-report/send-email", {
        year, month, to_addr: toAddr, subject, body,
      });
      setResult(r);
      if (r.sent) {
        setTimeout(() => onSent && onSent(), 800);
      }
    } catch (e) {
      setResult({ sent: false, error: e?.response?.data?.detail || e.message });
    } finally { setSending(false); }
  };

  return (
    <Modal open={true} onClose={onClose} title="E-posta Gönder" size="md">
      <div className="space-y-3">
        <Input label="Alıcı E-posta" value={toAddr} onChange={e => setToAddr(e.target.value)}
               data-testid="acc-email-to" />
        <Input label="Konu" value={subject} onChange={e => setSubject(e.target.value)}
               data-testid="acc-email-subject" />
        <Textarea label="Kısa Mesaj" value={body} onChange={e => setBody(e.target.value)}
                  rows={3} data-testid="acc-email-body" />
        {result && (
          <div className={`text-sm p-2 rounded ${result.sent ? "bg-emerald-50 text-emerald-800" : "bg-rose-50 text-rose-800"}`}>
            {result.sent ? "✓ Gönderildi" : `✗ Gönderilemedi: ${result.result?.error || result.error || "hata"}`}
          </div>
        )}
        <div className="flex justify-end gap-2">
          <Btn variant="secondary" onClick={onClose}>Vazgeç</Btn>
          <Btn onClick={send} disabled={sending} data-testid="acc-email-send-btn">
            {sending ? <RefreshCw size={14} className="animate-spin" /> : <Mail size={14} />}
            Gönder
          </Btn>
        </div>
      </div>
    </Modal>
  );
}

function TelegramModal({ year, month, onClose, onSent }) {
  const [chatId, setChatId] = useState("");
  const [caption, setCaption] = useState(`Aylık Personel Muhasebe Bildirimi - ${year}/${String(month).padStart(2, "0")}`);
  const [sending, setSending] = useState(false);
  const [result, setResult] = useState(null);

  useEffect(() => {
    api.get("/settings").then(cs => {
      const c = cs?.accountant_telegram_chat_id;
      if (c) setChatId(String(c));
    }).catch(() => {});
  }, []);

  const send = async () => {
    if (!chatId.trim()) return alert("Chat ID girin");
    setSending(true); setResult(null);
    try {
      const r = await api.post("/personnel/accounting-report/send-telegram", {
        year, month, chat_id: chatId, caption,
      });
      setResult(r);
      if (r.sent) {
        setTimeout(() => onSent && onSent(), 800);
      }
    } catch (e) {
      setResult({ sent: false, error: e?.response?.data?.detail || e.message });
    } finally { setSending(false); }
  };

  return (
    <Modal open={true} onClose={onClose} title="Telegram Gönder" size="md">
      <div className="space-y-3">
        <Input label="Chat ID" value={chatId} onChange={e => setChatId(e.target.value)}
               data-testid="acc-tg-chat-id" />
        <Input label="Başlık / Caption" value={caption} onChange={e => setCaption(e.target.value)}
               data-testid="acc-tg-caption" />
        {result && (
          <div className={`text-sm p-2 rounded ${result.sent ? "bg-emerald-50 text-emerald-800" : "bg-rose-50 text-rose-800"}`}>
            {result.sent ? "✓ Gönderildi" : `✗ Gönderilemedi: ${result.result?.error || result.error || "hata"}`}
          </div>
        )}
        <div className="flex justify-end gap-2">
          <Btn variant="secondary" onClick={onClose}>Vazgeç</Btn>
          <Btn onClick={send} disabled={sending} data-testid="acc-tg-send-btn">
            {sending ? <RefreshCw size={14} className="animate-spin" /> : <Send size={14} />}
            Gönder
          </Btn>
        </div>
      </div>
    </Modal>
  );
}
